/**
 * Store pour la gestion des rôles
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useAuthUserStore } from '@stores/auth';

export const useRoleStore = defineStore('role', (() => {

    // State des roles accessibles à l'utilisateur connecté (total, roles pour les clés)
    // Utilisé pour la gestion de la liste uniquement
    const roles = ref({});

    // State pour le détail des rôles (permissions) par identifiants de rôle
    const rolesById = ref({});

    // State pour les groupes et permissions (utilisé lors d'une création)
    const permissionsGroups = ref(null);

    /**
     * Retourne les groupes de permissions
     * @return array | null
     */
    const getPermissionGroups = computed(() => permissionsGroups.value);

    /**
     * Retourne les rôles accessibles à l'utilisateur connecté
     * @return array
     */
    const getRoles = computed(() => roles.value);

    /**
     * Retourne le rôle par son identifiant
     * @return object|null
     */
    const getById = computed(() => {
        return ((id) => {
            const role = rolesById.value[id] || null;
            return role;
        });
    });

    /**
     * Retourne les permissions du rôle
     * @param object role
     * @return object
     */
    const getPermissionsOf = computed(() => {
        return ((role) => {
            if(role.id === undefined) {
                return {};
            }
            const stateRole = rolesById.value[role.id] || {};
            return (stateRole.permissions || {});
        });
    });

    /**
     * Retourne les groupes de permissions éditables par l'utilisateur pour le rôle en paramètre
     * @param string id Identifiant du rôle
     * @return object
     */
    const getPermissionGroupsOf = computed(() => {
        const authUserStore = useAuthUserStore();

        return ((role) => {
            const roleId = role.id || null;
            let groups = getById.value(roleId).permissions.groups;
            const groupsKeys = Object.keys(groups);

            groupsKeys.forEach((crudType) => {
                let group = groups[crudType];
                const sectionKeys = Object.keys(group);

                let countSections = sectionKeys.length;
                sectionKeys.forEach((sectionName) => {
                    let section = group[sectionName];
                    const permissionsGroupKeys = Object.keys(section.groups);
                    let countPermissionGroups = permissionsGroupKeys.length;

                    permissionsGroupKeys.forEach((permissionGroupKey) => {
                        const permissionsKeys = Object.keys(section.groups[permissionGroupKey]);
                        let countPermissions = permissionsKeys.length;
                        permissionsKeys.forEach((permissionKey) => {
                            const grantPermission = section.groups[permissionGroupKey]?.grant?.id
                            const hasPermission = (grantPermission) ? authUserStore.hasPermission(grantPermission) : false;
                            if(! hasPermission) {
                                delete section.groups[permissionGroupKey][permissionKey];
                                countPermissions--;
                            }

                        });

                        // Supprime l'index de permission si l'utilisateur ne peux en accorder
                        if(countPermissions === 0) {
                            delete section.groups[permissionGroupKey];
                            countPermissionGroups--;
                        }

                    });

                    // Supprime le groupe de permissions si l'utilisateur ne peut en accorder
                    if(countPermissionGroups === 0) {
                        delete section.groups[sectionName];
                        countSections--;
                    }

                    // Supprime la section si le groupe est vide
                    if(Object.keys(section.groups).length === 0) {
                        delete group[sectionName];
                    }
                });

                // Supprime le groupe de permissions si l'utilisateur ne peut en accorder
                if(countSections === 0) {
                    delete groups[crudType];
                }
            });

            return groups;
        });
    });

    /**
     * Chargement des rôles accessibles à l'utilisateur connecté
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadRoles = ((params) => {

        const path = '/api/admin/roles';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetList();
                }

                updateList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement du rôle et de ses permissions
     * @param int roleId Identifiant du rôle à charger
     * @return Promise
     */
    const loadById = ((roleId) => {

        const role = getById.value(roleId);
        if(role !== null) {
            return Promise.resolve(role);
        }

        let path;

        if(roleId === null) {
            path = '/api/admin/roles/create';
        } else {
            path = '/api/admin/roles/:roleId'.replace(':roleId', roleId);
        }


        return axios.get(path).then((response) => {
            if (response && response.data && response.data.success && response.data.data) {
                updateRole(response.data.data);
                return Promise.resolve(response.data.data);
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Modifie le role dans la liste des rôles chargés avec permission
     * @param object role
     * @return void
     */
    const updateRole = ((role) => {
        rolesById.value[role.id] = role;
    });

    /**
     * Modifie les rôles
     * @param object data Données des rôles : total et items
     * @return void
     */
    const updateList = ((data) => {

        const currentData = roles.value.roles;
        if(! currentData) {
            roles.value = {
                total: data.total,
                roles: data.items
            };
        } else {
            roles.value.total = data.total;

            const currentRoles = roles.value.roles;
            const newRoles = data.items.filter((item) => ! currentRoles.find((current) => current.id === item.id));
            roles.value.roles = [ ...currentRoles, ...newRoles ];
        }

    });

    /**
     * Retire un rôle de la liste
     * @param object role Données du rôle à retirer de la liste
     * @return void
     */
    const removeRole = ((role) => {

        const path = '/api/admin/roles/:roleId'.replace(':roleId', role.id);

        return axios.delete(path).then((response) => {
            if (response && response.data && response.data.success) {

                const currentData = roles.value.roles;
                if (!currentData) {
                    return;
                }

                const countBefore = roles.value.roles.length;
                roles.value.roles = roles.value.roles.filter((current) => current.id !== role.id);
                const countAfter = roles.value.roles.length;

                roles.value.total += countAfter - countBefore;
            }
        });
    });

    /**
     * Réinitialisation de la liste des rôles accessibles à l'utilisateur connecté
     * @return void
     */
    const resetList = (() => {
        roles.value = {};
    });

    return {
        getRoles,
        loadRoles,
        removeRole,
        getById,
        loadById,
        updateRole,
        getPermissionsOf,
        getPermissionGroups,
        getPermissionGroupsOf,
        resetList
    };

}));
